import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, CheckCircle2, Circle, FileText, CreditCard, 
  Briefcase, Heart, Wallet, Package, Loader2, Trash2,
  Sparkles, AlertTriangle, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';

export default function TripChecklist() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { isOrganizer } = context || {};
  const { api } = useAuth();
  const { t } = useLanguage();

  // Category config with translations
  const categoryConfig = {
    documents: { 
      label: t('checklist.categories.documents'), 
      icon: FileText, 
      color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      description: t('checklist.categories.documentsDesc')
    },
    bookings: { 
      label: t('checklist.categories.bookings'), 
      icon: Calendar, 
      color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      description: t('checklist.categories.bookingsDesc')
    },
    packing: { 
      label: t('checklist.categories.packing'), 
      icon: Briefcase, 
      color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
      description: t('checklist.categories.packingDesc')
    },
    health: { 
      label: t('checklist.categories.health'), 
      icon: Heart, 
      color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      description: t('checklist.categories.healthDesc')
    },
    financial: { 
      label: t('checklist.categories.financial'), 
      icon: Wallet, 
      color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      description: t('checklist.categories.financialDesc')
    },
    other: { 
      label: t('checklist.categories.other'), 
      icon: Package, 
      color: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
      description: t('checklist.categories.otherDesc')
    },
  };
  
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    category: 'other',
    is_required: false,
    is_personal: true,
    due_date: '',
  });

  useEffect(() => {
    fetchData();
  }, [tripId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        api.get(`/trips/${tripId}/checklist`),
        api.get(`/trips/${tripId}/checklist/summary`)
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching checklist:', error);
      // If no items, try to initialize
      if (error.response?.status === 404 || items.length === 0) {
        handleInitialize();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    try {
      await api.post(`/trips/${tripId}/checklist/initialize`);
      toast.success('Checklist criado com itens padrão!');
      fetchData();
    } catch (error) {
      console.error('Error initializing checklist:', error);
    }
  };

  const handleToggle = async (itemId) => {
    try {
      const response = await api.put(`/trips/${tripId}/checklist/${itemId}/toggle`);
      
      setItems(items.map(item => 
        item.id === itemId 
          ? { ...item, completed: response.data.completed, completed_at: response.data.completed_at }
          : item
      ));
      
      // Update summary
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar item');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newItem.title) {
      toast.error('Digite o título do item');
      return;
    }

    try {
      await api.post(`/trips/${tripId}/checklist`, newItem);
      toast.success('Item adicionado!');
      setDialogOpen(false);
      setNewItem({
        title: '',
        description: '',
        category: 'other',
        is_required: false,
        is_personal: true,
        due_date: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Erro ao adicionar item');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Excluir este item?')) return;
    
    try {
      await api.delete(`/trips/${tripId}/checklist/${itemId}`);
      setItems(items.filter(item => item.id !== itemId));
      toast.success('Item excluído');
    } catch (error) {
      toast.error('Erro ao excluir item');
    }
  };

  const filteredItems = selectedCategory === 'all' 
    ? items 
    : items.filter(item => item.category === selectedCategory);

  const groupedItems = filteredItems.reduce((acc, item) => {
    const cat = item.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Checklist de Preparação"
        subtitle="Organize tudo que você precisa para a viagem"
        helpScreen="checklist"
        actions={
          <div className="flex gap-2">
            {items.length === 0 && (
              <Button
                variant="outline"
                onClick={handleInitialize}
                data-testid="initialize-checklist-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Criar checklist padrão
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-orange-500 hover:bg-orange-600" data-testid="add-checklist-item-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Novo item do checklist</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div>
                    <Label>Título *</Label>
                    <Input
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      placeholder="Ex: Levar protetor solar"
                      className="mt-1"
                      data-testid="checklist-item-title"
                    />
                  </div>

                  <div>
                    <Label>Categoria</Label>
                    <Select
                      value={newItem.category}
                      onValueChange={(v) => setNewItem({ ...newItem, category: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categoryConfig).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <config.icon className="w-4 h-4" />
                              {config.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Descrição (opcional)</Label>
                    <Input
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      placeholder="Detalhes adicionais..."
                      className="mt-1"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="is_required"
                      checked={newItem.is_required}
                      onCheckedChange={(checked) => setNewItem({ ...newItem, is_required: checked })}
                    />
                    <label htmlFor="is_required" className="text-sm text-slate-600 dark:text-slate-400">
                      Item obrigatório
                    </label>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600">
                      Adicionar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Progress Summary */}
      {summary && (
        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-orange-200 dark:border-orange-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  Progresso da preparação
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {summary.completed_items} de {summary.total_items} itens completados
                </p>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-orange-600">
                  {summary.completion_percentage}%
                </span>
              </div>
            </div>
            <Progress value={summary.completion_percentage} className="h-3" />
            
            {summary.overdue_items > 0 && (
              <div className="mt-4 flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {summary.overdue_items} item(s) com prazo vencido
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <Button
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          size="sm"
          className="rounded-full"
          onClick={() => setSelectedCategory('all')}
        >
          Todos
        </Button>
        {Object.entries(categoryConfig).map(([key, config]) => {
          const count = items.filter(i => i.category === key).length;
          if (count === 0) return null;
          return (
            <Button
              key={key}
              variant={selectedCategory === key ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() => setSelectedCategory(key)}
            >
              <config.icon className="w-4 h-4 mr-1" />
              {config.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Items by Category */}
      {items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              {t('checklist.noItems')}
            </h3>
            <p className="text-slate-500 mb-4">
              {t('checklist.createDefault')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([category, categoryItems]) => {
            const config = categoryConfig[category] || categoryConfig.other;
            const completedCount = categoryItems.filter(i => i.completed).length;
            
            return (
              <Card key={category}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center`}>
                        <config.icon className="w-4 h-4" />
                      </div>
                      {config.label}
                    </CardTitle>
                    <Badge variant="outline">
                      {completedCount}/{categoryItems.length}
                    </Badge>
                  </div>
                  <CardDescription>{config.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {categoryItems.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`flex items-center gap-3 p-3 rounded-lg group transition-colors ${
                          item.completed 
                            ? 'bg-green-50 dark:bg-green-900/10' 
                            : 'bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <button
                          onClick={() => handleToggle(item.id)}
                          className="flex-shrink-0"
                          data-testid={`toggle-${item.id}`}
                        >
                          {item.completed ? (
                            <CheckCircle2 className="w-6 h-6 text-green-500" />
                          ) : (
                            <Circle className="w-6 h-6 text-slate-300 hover:text-orange-500 transition-colors" />
                          )}
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${
                            item.completed 
                              ? 'text-slate-500 line-through' 
                              : 'text-slate-900 dark:text-white'
                          }`}>
                            {item.title}
                            {item.is_required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </p>
                          {item.description && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                          )}
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 text-red-500"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
